import { Grid } from "@material-ui/core";
import {
    Collections,
    Role,
    Round,
    Season,
    StatusRound,
    StatusRoundDescription,
    Track,
    useCollection,
} from "api";
import { DateTime } from "luxon";
import MaterialTable from "material-table";
import { useSnackbar } from "notistack";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useFirestore, WhereOptions } from "react-redux-firebase";
import { useHistory } from "react-router";
import {
    BoxCard,
    ConfirmationDialog,
    ConfirmationDialogState,
    LoggedUserContext,
    PageLayout,
    SeasonContext,
} from "ui/componentes";
import { LocalizationPTBr } from "util/MaterialTableUtil";
import {
    RaceControlAfterRaceFilter,
    RaceControlAfterRaceFilterState,
} from "./RaceControlAfterRaceFilter";
import { functions } from "config/firebaseConfig";

const clearConfirmation = (setConfirmation) => () =>
    setConfirmation({ title: "", message: "", showDialog: false });

const RaceControlAfterRaceView = () => {
    const currentSeason = useContext(SeasonContext);
    const tracksById =
        useCollection<{ [key: string]: Track }>({
            collection: Collections.TRACKS,
        }) || {};
    const [confirmation, setConfirmation] = useState<ConfirmationDialogState>({
        showDialog: false,
        message: "",
    });
    const seasonsById =
        useCollection<{ [key: string]: Season }>({
            collection: Collections.SEASONS,
        }) || {};
    const firestore = useFirestore();
    const { enqueueSnackbar } = useSnackbar();
    const loggedUser = useContext(LoggedUserContext);
    const history = useHistory();
    const [filter, setFilter] = useState<RaceControlAfterRaceFilterState>({
        seasonId: currentSeason.id,
        status: StatusRound.IN_PROGRESS,
    });

    const where = useMemo(() => {
        const where: WhereOptions[] = [
            ["date", "<=", DateTime.local().toFormat("yyyy-MM-dd")],
            ["seasonId", "==", filter.seasonId],
            ["status", "==", filter.status],
        ];
        return where;
    }, [filter]);

    const getTrackName = useCallback(
        (round) =>
            `${tracksById[round.trackId]?.friendlyName || ""} - ${
                round.trackCourse || ""
            }`,
        [tracksById]
    );

    let rounds =
        useCollection<Round[]>(
            {
                collection: Collections.ROUNDS,
                where,
                orderBy: ["date", "asc"],
                storeAs: `analysisAfterRace${filter.seasonId}${filter.status}`,
            },
            true
        ) || [];

    const championships = useCollection<object>({
        collection: Collections.CHAMPIONSHIPS,
    });

    const actions = useMemo(() => {
        const actions = [
            {
                tooltip: "Ir para análises pós corrida",
                icon: "description",
                onClick: (event, round) => {
                    history.push(`/race_control/${round["id"]}/true`);
                },
            },
        ];

        if (loggedUser.roles.includes(Role.ADMIN)) {
            actions.push({
                tooltip: "Encerrar etapa",
                icon: "done",
                onClick: (event, round) => {
                    const champName =
                        championships[round.championshipId]["name"];

                    setConfirmation(() => ({
                        title: champName,
                        message: `Tem certeza que deseja finalizar a etapa (${getTrackName(
                            round
                        )})?`,
                        showDialog: true,
                        onOk: () => {
                            return functions
                                .httpsCallable("results-calculateStandings")({
                                    championshipId: round.championshipId,
                                })
                                .then(() =>
                                    firestore
                                        .collection(Collections.ROUNDS)
                                        .doc(round.id)
                                        .update({ status: StatusRound.DONE })
                                )
                                .then(() => {
                                    enqueueSnackbar("Etapa finalizada!", {
                                        variant: "success",
                                    });
                                    clearConfirmation(setConfirmation)();
                                });
                        },
                    }));
                },
            });
        }

        return actions;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedUser.roles]);

    return (
        <PageLayout title="Análises pós corrida" showInBoxCard={false}>
            <ConfirmationDialog
                title={confirmation.title}
                showDialog={confirmation.showDialog}
                message={confirmation.message}
                onOk={confirmation.onOk}
                onCancel={clearConfirmation(setConfirmation)}
            />
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <RaceControlAfterRaceFilter onFilter={setFilter} />
                </Grid>
                <Grid item xs={12}>
                    <MaterialTable
                        components={{ Container: BoxCard }}
                        columns={[
                            {
                                title: "Data",
                                field: "date",
                                align: "center",
                                render: (round) =>
                                    DateTime.fromISO(round.date).toFormat(
                                        "dd/MM/yyyy"
                                    ),
                            },
                            {
                                title: "Campeonato",
                                field: "championship",
                                render: (round) => {
                                    if (
                                        championships &&
                                        championships[round.championshipId]
                                    ) {
                                        return championships[
                                            round.championshipId
                                        ]["name"];
                                    }
                                    return "";
                                },
                            },
                            {
                                title: "Etapa",
                                field: "trackId",
                                render: (round) => {
                                    return getTrackName(round);
                                },
                            },
                            {
                                title: "Temporada",
                                field: "season",
                                render: (round) => {
                                    const championship =
                                        championships &&
                                        championships[round.championshipId];

                                    if (!championship) return "";

                                    const season =
                                        seasonsById[championship.seasonId];

                                    if (!season) return "";

                                    return `${season.name} - ${season.year}`;
                                },
                            },
                            {
                                title: "Status",
                                field: "status",
                                render: (round) =>
                                    StatusRoundDescription[round.status],
                            },
                        ]}
                        localization={LocalizationPTBr}
                        data={rounds}
                        actions={actions}
                        options={{
                            showTitle: false,
                            paging: false,
                            sorting: false,
                            draggable: false,
                            search: false,
                        }}
                    />
                </Grid>
            </Grid>
        </PageLayout>
    );
};

export default RaceControlAfterRaceView;
export { RaceControlAfterRaceView };
